"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { apiFetch, tryRefreshToken, getActiveOrgSlug, ACTIVE_ORG_KEY } from "@/lib/api";
import type { AuthTokens, OrgSummary, User } from "@/lib/types";

interface ImpersonationTarget {
  name: string;
  email: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, name: string, turnstileToken?: string | null, termsAccepted?: boolean, inviteToken?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  refreshUser: () => Promise<void>;
  isImpersonating: boolean;
  /** True only for a SCOPED (no_sign) tenant break-glass session — home-org-only,
   *  no cross-tenant switching. False for full superadmin impersonation. */
  impersonationScoped: boolean;
  impersonationTarget: ImpersonationTarget | null;
  startImpersonation: (targetUserId: string, reason?: string) => Promise<void>;
  startOrgImpersonation: (orgId: string, memberId: string, reason?: string) => Promise<void>;
  exitImpersonation: () => void;
  // ── Workspace / org context (Phase 5) ──────────────────────────────────────
  /** All orgs the user is an active member of (from GET /api/organizations/me). */
  memberships: OrgSummary[];
  /** The currently active org (URL slug wins when on /o/<slug>; falls back to localStorage default). */
  activeOrg: OrgSummary | null;
  /** Switch the active org — validates slug ∈ memberships, persists to localStorage, reloads to /dashboard. */
  setActiveOrg: (slug: string) => void;
  /** Re-fetch memberships (e.g. after an invite is accepted). */
  refreshOrgs: () => Promise<void>;
  /** True once the first memberships fetch has settled (success OR failure). False during loading and after logout. */
  orgsLoaded: boolean;
  /** True if the last memberships fetch threw. False on success or while loading. */
  orgsError: boolean;
  /** Set (or clear) the user's default workspace. Null/empty clears. Re-syncs user.default_org_slug. */
  setDefaultWorkspace: (slug: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const TOKEN_KEY = "sf_access_token";
const REFRESH_KEY = "sf_refresh_token";
const REMEMBER_KEY = "sf_remember";
const IMPERSONATE_ADMIN_TOKEN_KEY = "sf_impersonate_admin_token";
const IMPERSONATE_TARGET_KEY = "sf_impersonate_target";

function getRememberPreference(): boolean {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(REMEMBER_KEY);
  return v !== "0"; // default true
}

function getStoredTokens() {
  if (typeof window === "undefined") return { access: null, refresh: null };
  // Try localStorage first (persistent), fall back to sessionStorage
  const access =
    localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  const refresh =
    localStorage.getItem(REFRESH_KEY) ?? sessionStorage.getItem(REFRESH_KEY);
  return { access, refresh };
}

export function storeTokens(tokens: AuthTokens, rememberMe?: boolean) {
  // If explicitly passed, persist the preference
  if (rememberMe !== undefined) {
    localStorage.setItem(REMEMBER_KEY, rememberMe ? "1" : "0");
  }
  const persist = rememberMe ?? getRememberPreference();
  const target = persist ? localStorage : sessionStorage;
  const other = persist ? sessionStorage : localStorage;
  // Write to chosen storage, clear from the other
  target.setItem(TOKEN_KEY, tokens.access_token);
  target.setItem(REFRESH_KEY, tokens.refresh_token);
  other.removeItem(TOKEN_KEY);
  other.removeItem(REFRESH_KEY);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  try { localStorage.removeItem("sf_amanda_conv"); } catch { /* ignore */ }
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

/** Decode JWT payload and check if token expires within `bufferSec` seconds. */
function isTokenExpired(token: string, bufferSec = 60): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now() + bufferSec * 1000;
  } catch {
    return true; // malformed → treat as expired
  }
}

function isCurrentlyImpersonating(): boolean {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem(IMPERSONATE_ADMIN_TOKEN_KEY);
}

/** True only for a SCOPED (no_sign) tenant break-glass impersonation. Superadmin
 *  (full-user) impersonation has no no_sign claim → false. Drives whether the
 *  session loads the target's memberships and can switch across their tenants:
 *  scoped break-glass stays home-org-only; full impersonation does not. */
function isNoSignImpersonation(): boolean {
  if (!isCurrentlyImpersonating()) return false;
  try {
    const { access } = getStoredTokens();
    if (!access) return false;
    const payload = JSON.parse(atob(access.split(".")[1]));
    return payload.no_sign === true;
  } catch {
    return false;
  }
}

function getStoredImpersonationTarget(): ImpersonationTarget | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(IMPERSONATE_TARGET_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Derive the active org for FLAT landing. Per-tab /o/<slug> URLs and email
 *  deep-links are resolved upstream and always win over this. */
function resolveActiveOrg(
  memberships: OrgSummary[],
  userHomeOrg: OrgSummary | null | undefined,
  defaultSlug?: string | null,
): OrgSummary | null {
  if (memberships.length === 0) return userHomeOrg ?? null;

  // 1. Explicit default workspace — honored only if still an active member.
  if (defaultSlug) {
    const dflt = memberships.find((m) => m.slug === defaultSlug);
    if (dflt) return dflt;
  }
  // 2. localStorage last-used (unchanged; only reached when no valid default).
  const storedSlug = typeof window !== "undefined" ? localStorage.getItem(ACTIVE_ORG_KEY) : null;
  if (storedSlug) {
    const found = memberships.find((m) => m.slug === storedSlug);
    if (found) return found;
  }
  // 3. Home org, then 4. first membership.
  if (userHomeOrg) {
    const homeInList = memberships.find((m) => m.id === userHomeOrg.id);
    if (homeInList) return homeInList;
  }
  return memberships[0];
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(isCurrentlyImpersonating);
  const [impersonationScoped, setImpersonationScoped] = useState(isNoSignImpersonation);
  const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(getStoredImpersonationTarget);
  const [memberships, setMemberships] = useState<OrgSummary[]>([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [orgsError, setOrgsError] = useState(false);
  const router = useRouter();
  const refreshingRef = useRef<Promise<boolean> | null>(null);

  const getAccessToken = useCallback(() => {
    return getStoredTokens().access;
  }, []);

  // Silent token refresh — deduplicates concurrent calls
  // Skip during impersonation (no refresh token available)
  const silentRefresh = useCallback(async (): Promise<boolean> => {
    if (isCurrentlyImpersonating()) return false;
    if (refreshingRef.current) return refreshingRef.current;

    refreshingRef.current = (async () => {
      const { token: newToken } = await tryRefreshToken();
      if (!newToken) return false;
      try {
        const me = await apiFetch<User>("/auth/me", { token: newToken });
        setUser(me);
        return true;
      } catch {
        return false;
      }
    })();

    try {
      return await refreshingRef.current;
    } finally {
      refreshingRef.current = null;
    }
  }, []);

  // Load user on mount if tokens exist
  useEffect(() => {
    const { access } = getStoredTokens();
    if (!access) {
      setIsLoading(false);
      return;
    }

    // Restore impersonation state on mount
    if (isCurrentlyImpersonating()) {
      setIsImpersonating(true);
      setImpersonationScoped(isNoSignImpersonation());
      setImpersonationTarget(getStoredImpersonationTarget());
    }

    if (isTokenExpired(access)) {
      if (isCurrentlyImpersonating()) {
        // Impersonation token expired — exit impersonation
        clearImpersonation();
        setIsLoading(false);
        return;
      }
      silentRefresh()
        .then((ok) => { if (!ok) { clearTokens(); window.sfAnalytics?.track("token_refresh_failed", "Auth"); } })
        .finally(() => setIsLoading(false));
    } else {
      apiFetch<User>("/auth/me", { token: access })
        .then(setUser)
        .catch(() => {
          if (isCurrentlyImpersonating()) {
            clearImpersonation();
            return;
          }
          return silentRefresh().then((ok) => { if (!ok) { clearTokens(); window.sfAnalytics?.track("token_refresh_failed", "Auth"); } });
        })
        .finally(() => setIsLoading(false));
    }
  }, [silentRefresh]);

  // Proactive refresh: when tab becomes visible or every 4 minutes
  // Skip during impersonation
  useEffect(() => {
    const checkAndRefresh = async () => {
      if (isCurrentlyImpersonating()) return;
      const { access } = getStoredTokens();
      if (!access) return;
      // Refresh if token expires within 2 minutes
      if (isTokenExpired(access, 120)) {
        const ok = await silentRefresh();
        if (!ok) {
          clearTokens();
          setUser(null);
          window.sfAnalytics?.track("token_refresh_failed", "Auth");
        }
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") checkAndRefresh();
    };

    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(checkAndRefresh, 4 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [silentRefresh]);

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean) => {
      const tokens = await apiFetch<AuthTokens>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      storeTokens(tokens, rememberMe);
      const me = await apiFetch<User>("/auth/me", {
        token: tokens.access_token,
      });
      setUser(me);
      window.sfAnalytics?.track("login", "Auth");
      // Flat /dashboard intentional — middleware 307s it to /o?next=/dashboard → /o resolver → /o/<slug>/dashboard.
      // Prefixing here would require memberships to be loaded; middleware + /o resolver is more robust at login time.
      router.push(me.terms_accepted_at ? "/dashboard" : "/accept-terms");
    },
    [router]
  );

  const register = useCallback(
    async (email: string, password: string, name: string, turnstileToken?: string | null, termsAccepted?: boolean, inviteToken?: string | null) => {
      await apiFetch<User>("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          name,
          turnstile_token: turnstileToken,
          terms_accepted: termsAccepted ?? false,
          ...(inviteToken ? { invite_token: inviteToken } : {}),
        }),
      });
      window.sfAnalytics?.track("register", "Auth");
      // Redirect to verify-email page instead of auto-login
      router.push(`/verify-email?email=${encodeURIComponent(email)}`);
    },
    [router]
  );

  const refreshUser = useCallback(async () => {
    const { access } = getStoredTokens();
    if (!access) return;
    try {
      const me = await apiFetch<User>("/auth/me", { token: access });
      setUser(me);
    } catch {
      // Silently fail — user state will be stale but not broken
    }
  }, []);

  /** Fetch the user's org memberships and keep them in sync. */
  const refreshOrgs = useCallback(async () => {
    // Skip org-load ONLY for a SCOPED (no_sign) break-glass session — it must stay
    // home-org-only. Full superadmin impersonation loads the target's memberships
    // so the switcher shows their tenants and cross-tenant switching works.
    if (isNoSignImpersonation()) return;
    const { access } = getStoredTokens();
    if (!access) return;
    setOrgsError(false);
    try {
      const orgs = await apiFetch<OrgSummary[]>("/organizations/me", { token: access });
      setMemberships(orgs);
    } catch {
      setOrgsError(true);
    } finally {
      setOrgsLoaded(true);
    }
  }, []);

  // Load orgs whenever the user changes (login, refresh, impersonation exit)
  useEffect(() => {
    if (user && !isNoSignImpersonation()) {
      refreshOrgs();
    }
    if (!user) {
      setMemberships([]);
      setOrgsLoaded(false);
      setOrgsError(false);
    }
  }, [user, refreshOrgs]);

  /** Derive activeOrg from current URL slug (per-tab) or localStorage default. */
  const pathname = usePathname();
  const activeOrg = useMemo(() => {
    // When on an /o/<slug>/... workspace URL, the URL slug wins — this makes the
    // switcher label and externalBilling flag correct per-tab even in multi-org scenarios.
    const urlSlug = pathname?.match(/^\/o\/([^/]+)/)?.[1];
    if (urlSlug) {
      const fromUrl = memberships.find((m) => m.slug === urlSlug);
      if (fromUrl) return fromUrl; // per-tab: URL workspace wins
    }
    // Flat/off-workspace pages: resolve landing — explicit default → localStorage → home → first.
    return resolveActiveOrg(memberships, user?.org, user?.default_org_slug);
  }, [pathname, memberships, user?.org, user?.default_org_slug]);

  // Keep localStorage in sync with the resolved activeOrg.
  // Only run AFTER memberships have loaded (memberships.length > 0) to avoid
  // overwriting a valid stored slug with the home-org fallback during the initial
  // empty-memberships render cycle.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (memberships.length === 0) return; // Wait until memberships are loaded
    if (activeOrg) {
      localStorage.setItem(ACTIVE_ORG_KEY, activeOrg.slug);
    }
  }, [activeOrg, memberships.length]);

  // Re-resolve entitlements when the acting org changes.
  //
  // `tier` and `tier_limits` are computed SERVER-side against the acting org
  // (the X-Active-Org header), so a /auth/me response is only true for the org
  // it was fetched under. The acting org, however, also moves on client-side
  // navigation between workspaces — which does not remount this provider — so
  // the cached `user` went on describing the PREVIOUS org. `setActiveOrg`
  // sidesteps it by hard-reloading; every other route into a workspace did not.
  //
  // The visible failure: log in on a flat URL (entitlements resolve against the
  // home/personal org), then soft-navigate to /o/<enterprise-org>/…. The
  // switcher shows the right workspace and the URL is right, but every
  // canUseFeature() gate still answers for the personal org — silently hiding
  // enterprise-only features such as AES.
  const entitlementOrgRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!user) return;
    // Wait for memberships to settle. Before they load, activeOrg is briefly
    // null, and treating that as "the org changed" costs two needless /auth/me
    // round trips per page load — one for the transition to null and one back.
    if (!orgsLoaded) return;
    const slug = activeOrg?.slug ?? null;
    if (!slug) return;
    if (entitlementOrgRef.current === undefined) {
      // First resolution after boot. The bootstrap /auth/me already sent
      // whatever getActiveOrgSlug() returned, so only refetch when the resolved
      // workspace actually differs from it — no extra request in the common case.
      entitlementOrgRef.current = getActiveOrgSlug();
    }
    if (entitlementOrgRef.current === slug) return;
    entitlementOrgRef.current = slug;
    refreshUser();
  }, [user, orgsLoaded, activeOrg?.slug, refreshUser]);

  /** Switch the active workspace. Validates slug ∈ memberships, then hard-reloads. */
  const setActiveOrg = useCallback((slug: string) => {
    const target = memberships.find((m) => m.slug === slug);
    if (!target) return; // unknown slug — ignore
    if (typeof window !== "undefined") {
      localStorage.setItem(ACTIVE_ORG_KEY, slug);
    }
    // Hard reload to the org-scoped dashboard so the per-tab URL slug drives X-Active-Org
    window.location.assign(`/o/${slug}/dashboard`);
  }, [memberships]);

  /** Set (or clear) the user's default workspace. Null/empty clears. Re-syncs user.default_org_slug. */
  const setDefaultWorkspace = useCallback(async (slug: string | null) => {
    const { access: token } = getStoredTokens();
    if (!token) return;
    try {
      await apiFetch("/auth/me/preferences", {
        method: "PUT",
        body: JSON.stringify({ default_workspace_slug: slug ?? "" }),
        token,
      });
      // Re-fetch /auth/me so user.default_org_slug is authoritative.
      const { access: freshToken } = getStoredTokens();
      if (freshToken) {
        const me = await apiFetch<User>("/auth/me", { token: freshToken });
        setUser(me);
      }
    } catch (err) {
      console.error("[setDefaultWorkspace] failed:", err);
    }
  }, []);

  /** Restore admin's original token and clear impersonation state */
  function clearImpersonation() {
    const adminToken = localStorage.getItem(IMPERSONATE_ADMIN_TOKEN_KEY);
    if (adminToken) {
      // Restore admin's access token
      localStorage.setItem(TOKEN_KEY, adminToken);
      sessionStorage.removeItem(TOKEN_KEY);
    }
    localStorage.removeItem(IMPERSONATE_ADMIN_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATE_TARGET_KEY);
    setIsImpersonating(false);
    setImpersonationScoped(false);
    setImpersonationTarget(null);
  }

  const startImpersonation = useCallback(async (targetUserId: string, reason?: string) => {
    const adminToken = getAccessToken();
    if (!adminToken) return;

    const data = await apiFetch<{
      access_token: string;
      target_user_id: string;
      target_name: string;
      target_email: string;
      expires_minutes: number;
    }>(`/admin/users/${targetUserId}/impersonate`, {
      method: "POST",
      token: adminToken,
      body: JSON.stringify({ reason: reason || null }),
    });

    // Save admin's current token for later restoration
    localStorage.setItem(IMPERSONATE_ADMIN_TOKEN_KEY, adminToken);

    // Save target info
    const target: ImpersonationTarget = { name: data.target_name, email: data.target_email };
    localStorage.setItem(IMPERSONATE_TARGET_KEY, JSON.stringify(target));

    // Swap to impersonation token (no refresh token)
    localStorage.setItem(TOKEN_KEY, data.access_token);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(REFRESH_KEY);

    // Load target user profile
    const me = await apiFetch<User>("/auth/me", { token: data.access_token });
    setUser(me);
    setIsImpersonating(true);
    setImpersonationScoped(false); // full superadmin impersonation — cross-tenant switching allowed
    setImpersonationTarget(target);

    router.push("/dashboard");
  }, [getAccessToken, router]);

  const startOrgImpersonation = useCallback(async (orgId: string, memberId: string, reason?: string) => {
    const adminToken = getAccessToken();
    if (!adminToken) return;

    const data = await apiFetch<{
      access_token: string;
      target_user_id: string;
      target_name: string;
      target_email: string;
      expires_minutes: number;
    }>(`/organizations/${orgId}/members/${memberId}/impersonate`, {
      method: "POST",
      token: adminToken,
      body: JSON.stringify({ reason: reason || null }),
    });

    localStorage.setItem(IMPERSONATE_ADMIN_TOKEN_KEY, adminToken);
    const target: ImpersonationTarget = { name: data.target_name, email: data.target_email };
    localStorage.setItem(IMPERSONATE_TARGET_KEY, JSON.stringify(target));

    localStorage.setItem(TOKEN_KEY, data.access_token);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(REFRESH_KEY);

    const me = await apiFetch<User>("/auth/me", { token: data.access_token });
    setUser(me);
    setIsImpersonating(true);
    setImpersonationScoped(true); // scoped break-glass (no_sign) — home-org-only, no cross-tenant switching
    setImpersonationTarget(target);

    router.push("/dashboard");
  }, [getAccessToken, router]);

  const exitImpersonation = useCallback(() => {
    clearImpersonation();
    // Hard reload to fully reset all state back to admin
    window.location.href = "/admin/users";
  }, []);

  const logout = useCallback(async () => {
    // If impersonating, just exit impersonation instead of real logout
    if (isCurrentlyImpersonating()) {
      clearImpersonation();
      window.location.href = "/admin/users";
      return;
    }

    const { refresh } = getStoredTokens();
    if (refresh) {
      await apiFetch("/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refresh_token: refresh }),
      }).catch(() => {});
    }
    window.sfAnalytics?.track("logout", "Auth");
    clearTokens();
    // Clear active org so the next login starts fresh
    if (typeof window !== "undefined") {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
    setUser(null);
    setMemberships([]);
    router.push("/login");
  }, [router]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      getAccessToken,
      refreshUser,
      isImpersonating,
      impersonationScoped,
      impersonationTarget,
      startImpersonation,
      startOrgImpersonation,
      exitImpersonation,
      // Phase 5 — workspace / org
      memberships,
      activeOrg,
      setActiveOrg,
      refreshOrgs,
      orgsLoaded,
      orgsError,
      setDefaultWorkspace,
    }),
    [user, isLoading, login, register, logout, getAccessToken, refreshUser, isImpersonating, impersonationScoped, impersonationTarget, startImpersonation, startOrgImpersonation, exitImpersonation, memberships, activeOrg, setActiveOrg, refreshOrgs, orgsLoaded, orgsError, setDefaultWorkspace]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
